import type { ComponentChildren } from 'preact'
import styles from './OptionalSection.module.css'

/**
 * Something the user may open, closed until they do (D-104 §2).
 *
 * **A native `<details>`, not a bespoke disclosure**, for the reasons C6.3
 * recorded when Settings chose the same thing: keyboard-operable with no
 * handler, announced as expanded or collapsed without an `aria-expanded` anyone
 * has to remember to update, findable by the browser's own in-page search while
 * closed, and no JavaScript at all.
 *
 * **This is for optional input and secondary detail, not for explanation.** An
 * explanation goes behind an `InfoButton`, which opens a sheet and states what
 * it is; a disclosure whose summary reads "Why this matters" is an explanation
 * wearing a disclosure's clothes, and the user has to open it to find out
 * whether it was worth opening.
 *
 * **A closed `<details>` keeps its contents in the DOM.** Any test asserting
 * that something is hidden here must use visibility rather than presence — a
 * `toHaveCount(0)` or a `not.toContainText` against a closed section passes
 * whatever it holds, which is the guard-that-cannot-fail C7.0 phase 4 shipped
 * and this comment exists to stop repeating.
 *
 * **It must not be the destination of a prompt on another screen.** C6.3 found a
 * real dead end when Today's "Back up now" landed on a Settings page whose
 * backup controls were collapsed: *"a collapse that hides the destination of a
 * prompt is not tidiness."*
 */
interface OptionalSectionProps {
  /** The summary line. A noun phrase for what is inside, never a question. */
  label: string
  /**
   * Open on first render.
   *
   * For the C6.3 case only — a section something else sends the user to. It is
   * not remembered between visits, deliberately: a stored open state is a
   * preference nobody set and a record nobody asked for.
   */
  defaultOpen?: boolean
  children: ComponentChildren
}

export function OptionalSection({ label, defaultOpen = false, children }: OptionalSectionProps) {
  return (
    <details class={styles.section} open={defaultOpen}>
      <summary class={styles.summary}>{label}</summary>
      <div class={styles.body}>{children}</div>
    </details>
  )
}
